import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {InputNumberGroupComponent} from '@brunata-angular/ui';
import {TranslatePipe} from '@brunata-angular/util';
import {ExportNoteComponent} from '../../shared/ui/export-note/export-note.component';
import {OptionSelectComponent} from '../../shared/ui/option-select/option-select.component';
import {
    ReadingResolution,
    exportWeights,
    readingResolutionOptions,
} from '../../shared/models/reading-shape.model';
import {StepCardDirective} from '../step-card/step-card.directive';
import {stepLabels} from '../step-card/step-labels.model';

@Component({
    selector: 'app-reading-shape-step',
    standalone: true,
    providers: [
        stepLabels(
            'customizedexports_export_step_reading_shape_title',
            'customizedexports_export_step_reading_shape_hint',
        ),
    ],
    hostDirectives: [{directive: StepCardDirective, inputs: ['titleKey', 'hintKey']}],
    imports: [
        ExportNoteComponent,
        InputNumberGroupComponent,
        OptionSelectComponent,
        ReactiveFormsModule,
        TranslatePipe,
    ],
    template: `
        <div class="reading-shape-panel border">
            <div class="p-3">
                <div class="fw-bold required mb-2">
                    {{
                        'customizedexports_export_reading_resolution_label'
                            | translate: 'online' : 'export'
                    }}
                </div>
                <div class="resolution-box">
                    <app-option-select
                        [options]="resolutionOptions"
                        [selectedId]="readingResolution()"
                        name="customized-export-reading-resolution"
                        ariaKey="customizedexports_export_reading_resolution_label"
                        layout="row"
                        (optionSelected)="resolutionSelected.emit($event)" />
                    <div
                        class="progress weight-track"
                        role="progressbar"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        [attr.aria-valuenow]="weight().filledPercent"
                        [attr.aria-label]="
                            'customizedexports_export_reading_weight_label'
                                | translate: 'online' : 'export'
                        "
                        [class.tone-warning]="weight().tone === 'warning'"
                        [class.tone-danger]="weight().tone === 'danger'">
                        <div class="progress-bar" [style.width.%]="weight().filledPercent"></div>
                    </div>
                </div>
            </div>

            <div class="border-top p-3">
                <label class="fw-bold required mb-1" for="days-back-in-time">
                    {{
                        'customizedexports_export_reading_days_back_label'
                            | translate: 'online' : 'export'
                    }}
                </label>
                <p class="text-muted small mb-2">
                    {{
                        'customizedexports_export_reading_days_back_hint'
                            | translate: 'online' : 'export'
                    }}
                </p>
                <app-input-number-group
                    class="days-back-field d-block"
                    id="days-back-in-time"
                    [descriptor]="
                        'customizedexports_export_reading_days_back_suffix'
                            | translate: 'online' : 'export'
                    "
                    [formControl]="daysBackInTimeControl()" />
            </div>

            @if (everyReading()) {
                <div class="border-top p-3">
                    <app-export-note icon="bi-exclamation-triangle-fill" tone="warning">
                        <p class="fw-bold mb-1">
                            {{
                                'customizedexports_export_reading_high_resolution_title'
                                    | translate: 'online' : 'export'
                            }}
                        </p>
                        <p class="mb-0">
                            {{
                                'customizedexports_export_reading_high_resolution_body'
                                    | translate: 'online' : 'export'
                            }}
                        </p>
                    </app-export-note>
                </div>
            }
        </div>

        <app-export-note class="d-block mt-3" icon="bi-info-circle-fill">
            <p class="mb-0">
                {{
                    'customizedexports_export_reading_shape_info_channels'
                        | translate: 'online' : 'export'
                }}
            </p>
        </app-export-note>
    `,
    styles: `
        .resolution-box {
            --option-list-border: 0;
            border: 1px solid rgba(var(--bs-primary-rgb), 0.3);
        }

        .weight-track {
            --bs-progress-height: 0.1875rem;
            --bs-progress-border-radius: 0;
            --bs-progress-bg: rgba(var(--bs-primary-rgb), 0.08);

            &.tone-warning {
                --bs-progress-bar-bg: var(--bs-warning);
            }

            &.tone-danger {
                --bs-progress-bar-bg: var(--bs-danger);
            }
        }

        .reading-shape-panel {
            --bs-border-color: rgba(var(--bs-primary-rgb), 0.15);
        }

        .days-back-field {
            max-width: 16rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReadingShapeStepComponent {
    readonly readingResolution = input.required<ReadingResolution>();
    readonly daysBackInTimeControl = input.required<FormControl<number | null>>();

    readonly resolutionSelected = output<string>();

    protected readonly resolutionOptions = readingResolutionOptions;

    protected readonly weight = computed(() => exportWeights[this.readingResolution()]);

    protected readonly everyReading = computed(() => this.readingResolution() !== 'DAILY');
}
